import { SettingsSaveModule } from './settings-save.js';
/**
 * The language a player who has never chosen one should get.
 *
 * ## Why this is platform-independent code
 *
 * Module 41 (WP-41.7) built locale detection for the CrazyGames embed only, out
 * of CrazyGames' own requirement — read the locale from the SDK's system info,
 * fall back to English. Everywhere else the app simply started in English, so a
 * Hindi player on the main site or on Android had to find Settings before the
 * product spoke their language, even though the browser had been telling us
 * which language they read the whole time.
 *
 * The rule is the same on every platform, so the rule lives in one place. What
 * differs is only where the *tag* comes from — a portal SDK on CrazyGames, the
 * browser/OS everywhere else — and that is a single argument, not a fork.
 *
 * ## The precedence rule, and why it is the interesting part
 *
 * Detection supplies the **initial** value only. Once the player has picked a
 * language in Settings, that wins forever — including when they pick English on
 * a Hindi browser, which is exactly the case a naive "detect on every boot"
 * implementation silently overrides. Their choice would be undone on every
 * reload and they would have no way to make it stick.
 *
 * This is why {@link SettingsSaveModule.hasStored} exists: `load()` merges the
 * `en` default in, so it cannot tell a first-time visitor from someone who
 * deliberately chose English.
 *
 * ## Detection persists, as of Module 46 (WP-46.2) — and why that reversed
 *
 * This module used to argue the opposite, and the argument was a good one:
 * writing a *detected* value makes it indistinguishable from a real choice, and
 * detection then never runs again — so a player whose first load had a blocked
 * SDK or an exotic `navigator.language` would be pinned to a bad guess for good,
 * with the guess looking exactly like a decision they made. Detection is cheap,
 * so it re-ran every boot until the player actually chose.
 *
 * The product requirement is the other one: detection happens **once**, and from
 * then on the stored language is authoritative. Re-detecting every boot fails
 * the case that motivated it — a player who is given Hindi, is happy with Hindi,
 * never opens Settings, and then reads the game in English because they opened
 * it on a work laptop. "The language stops changing under you" is worth more
 * than "a bad first guess self-corrects", and the bad guess is one Settings
 * visit away either way.
 *
 * So the **first fully-resolved** language is written to storage immediately and
 * a stored language gates everything after it.
 *
 * ## Detection carries provenance, as of Module 59 — the certification lesson
 *
 * WP-46.2's own warning ("a detected value becomes indistinguishable from a
 * real choice") came due in Playgama's Guided Certification: the Localization
 * step switches the platform language to German after the tester has already
 * played, and the boot-one detected English refused it — for every tester,
 * every time, because the tool's flow guarantees stored state by that step.
 *
 * The resolution keeps both requirements instead of trading them: the stored
 * language carries `languageSource`, a real choice (`'chosen'`, written only by
 * {@link persistChosenLanguage}) wins forever exactly as before, and a stored
 * *detection* yields to a PLATFORM that currently says otherwise — but never to
 * the browser, so the work-laptop case that motivated detect-once persistence
 * still holds everywhere a platform locale does not exist.
 *
 * ## "Fully resolved" is the trap
 *
 * On most platforms detection is one step, so "first resolved" and "first
 * computed" are the same moment. On CrazyGames they are not: the app resolves a
 * browser-derived guess synchronously so the first paint is not in the wrong
 * language, and only afterwards — once `sdk.user.getSystemInfo()` has answered —
 * learns the locale that is supposed to *outrank* the browser. Persisting at the
 * first of those two moments would lock in the guess and mean the SDK locale,
 * which CrazyGames requires be preferred, never wins on the one boot where it
 * could have.
 *
 * Hence {@link InitialLanguageOptions.persist}: the early, render-only call opts
 * out, and the later call that actually knows the answer is the one that writes.
 * A platform declares which case it is in through its `providesLocale`
 * capability, so no shared module has to know it is on CrazyGames.
 */
/** The stored settings field this module owns. */
const LANGUAGE_FIELD = 'language';
/** Its provenance sibling — see {@link SettingsData.languageSource}. */
const LANGUAGE_SOURCE_FIELD = 'languageSource';
/**
 * Lowercase a tag and normalise its separator, so `HI_in`, `hi-IN` and `hi-in`
 * are one string. Returns `''` for anything with no content to match on.
 */
function normalizeTag(tag) {
    return tag.trim().toLowerCase().replace(/_/g, '-');
}
/** The part before the first separator — `pt` for `pt-BR`, `hi` for `hi-IN`. */
function primarySubtag(normalized) {
    return normalized.split('-')[0] ?? '';
}
/**
 * Narrow a BCP-47 tag to one of the locales the running platform actually ships.
 *
 * Two passes, in this order:
 *
 * 1. **Exact tag.** `pt-BR` asked for, `pt-BR` shipped — take it. This pass
 *    exists because not every shipped locale is a bare primary subtag: the
 *    Portuguese in the registry is specifically Brazilian, and a primary-subtag
 *    match alone would compare `pt` against `pt-BR`, fail, and hand every
 *    Brazilian player English while looking perfectly correct.
 * 2. **Primary subtag.** `hi-IN`, `hi_IN` and `hi` all resolve to `hi`; `pt-PT`
 *    resolves to `pt-BR`, which is the only Portuguese there is to offer and a
 *    far better answer than English. A region-qualified tag that fails to match
 *    anything is the most likely way this quietly stops working, and every real
 *    source (a browser's `navigator.language`, CrazyGames' system info)
 *    region-qualifies. Case-insensitive throughout for the same reason.
 *
 * `supported` is a parameter rather than a literal here: the platform registry
 * already states which locales a platform ships, and a second list would drift
 * from it (rule 1 of `Platform_Export_Rules.md`).
 *
 * @returns a supported locale, always — the first entry is the required fallback.
 */
export function resolveLocale(tag, supported) {
    const fallback = supported[0] ?? 'en';
    if (!tag)
        return fallback;
    const normalized = normalizeTag(tag);
    if (!normalized)
        return fallback;
    const exact = supported.find((s) => normalizeTag(s) === normalized);
    if (exact)
        return exact;
    const primary = primarySubtag(normalized);
    if (!primary)
        return fallback;
    return supported.find((s) => primarySubtag(normalizeTag(s)) === primary) ?? fallback;
}
/**
 * The player's locale according to the browser/OS, or null when there is none
 * to read.
 *
 * `navigator.languages[0]` before `navigator.language`: the former is the
 * ordered preference list the player actually configured, and the two disagree
 * on exactly the interesting case — a browser whose UI is English but whose
 * preference list starts with Hindi. Both are read defensively because this
 * runs inside a Capacitor WebView and inside a portal iframe, neither of which
 * is obliged to look like a desktop browser.
 */
export function detectBrowserLocale() {
    if (typeof navigator === 'undefined')
        return null;
    const list = navigator.languages;
    const first = Array.isArray(list)
        ? list.find((tag) => typeof tag === 'string' && tag)
        : undefined;
    return first ?? navigator.language ?? null;
}
/**
 * Module 80 (WP-80.13) — every language the browser lists, in its order, with
 * `navigator.language` as the one-entry fallback. Never the country.
 */
export function detectBrowserLocales() {
    if (typeof navigator === 'undefined')
        return [];
    const list = Array.isArray(navigator.languages)
        ? navigator.languages.filter((tag) => typeof tag === 'string' && tag !== '')
        : [];
    if (list.length > 0)
        return list;
    return navigator.language ? [navigator.language] : [];
}
/**
 * Module 80 (WP-80.13) — whether a tag names a language we ship, by exact tag
 * or primary subtag, WITHOUT falling back to English the way `resolveLocale`
 * does. `ta-IN` is not supported; `hi-IN` is `hi`.
 */
function supportedMatch(tag, supported) {
    const normalized = normalizeTag(tag);
    if (!normalized)
        return null;
    const exact = supported.find((s) => normalizeTag(s) === normalized);
    if (exact)
        return exact;
    const primary = primarySubtag(normalized);
    return supported.find((s) => primarySubtag(normalizeTag(s)) === primary) ?? null;
}
/**
 * Module 80 (WP-80.13) — the browser's answer from its WHOLE list: the first
 * entry we ship. Before this only the first entry was read, so `['ta-IN', 'hi']`
 * resolved to English. `['en-IN', 'hi']` is still English — the player put it
 * first — and is what the first-visit language prompt is for.
 */
export function resolveBrowserLocales(tags, supported) {
    for (const tag of tags) {
        const match = supportedMatch(tag, supported);
        if (match)
            return match;
    }
    return null;
}
/** Module 80 (WP-80.14) — IANA zones whose main language we ship, used ONLY to ask. */
const ASK_BY_TIME_ZONE = Object.freeze({
    'Asia/Kolkata': 'hi',
    'Asia/Calcutta': 'hi',
});
function currentTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    }
    catch {
        return null;
    }
}
/**
 * Module 80 (WP-80.14) — should a first-visit player be ASKED whether they want
 * another language, and which?
 *
 * Only when the game is rendering English and the player has not CHOSEN it.
 * The suggestion is the first non-English language we ship from the browser's
 * list, else the main language of the device's time zone. The time zone (like
 * country) is never allowed to SET a language — `Localization_Rules.md` v1.4:
 * a Tamil speaker in Kolkata must not be switched to Hindi — but it may decide
 * whether a one-tap question is worth asking, because the answer is the
 * player's own.
 */
export function languageSuggestion(supported, current, env = {}) {
    const fallback = supported[0] ?? 'en';
    if (current !== fallback)
        return null;
    const stored = SettingsSaveModule.loadStored();
    if (stored[LANGUAGE_FIELD] !== undefined && stored[LANGUAGE_SOURCE_FIELD] === 'chosen') {
        return null;
    }
    const tags = env.browserTags ?? detectBrowserLocales();
    for (const tag of tags) {
        const match = supportedMatch(tag, supported);
        if (match && match !== fallback)
            return match;
    }
    const zone = env.timeZone === undefined ? currentTimeZone() : env.timeZone;
    const byZone = zone ? ASK_BY_TIME_ZONE[zone] : undefined;
    return byZone && supported.includes(byZone) ? byZone : null;
}
/**
 * Store a detected language, tolerating a storage layer that refuses.
 *
 * `SettingsSaveModule.save` calls `localStorage.setItem`, which throws in Safari
 * private browsing and when the origin is over quota. That was survivable while
 * the only caller was a Settings click handler; this one runs during boot, where
 * an uncaught throw takes the whole app down before first paint. Failing to
 * remember the language is a far smaller problem than failing to start, and the
 * next boot simply detects again.
 */
function persistDetected(language) {
    try {
        SettingsSaveModule.save({ [LANGUAGE_FIELD]: language, [LANGUAGE_SOURCE_FIELD]: 'detected' });
    }
    catch {
        /* storage unavailable — detection re-runs next boot, which is the old behaviour */
    }
}
/**
 * Module 59 — the ONE way a player's explicit language pick is stored.
 *
 * Every language picker calls this instead of writing `{ language }` raw,
 * because the raw write is indistinguishable from detect-once persistence —
 * and that indistinguishability was a shipped certification failure:
 * Playgama's Localization step switches the platform language to German, and
 * the first boot's detected English blocked it forever, exactly as if the
 * player had decided. Provenance is what lets {@link initialLanguage} keep
 * "a choice wins forever" while a stored *detection* stays supersedable by
 * the platform.
 *
 * Throws like `SettingsSaveModule.save` does: callers are click handlers,
 * where a storage failure surfacing is the pre-existing behaviour.
 */
export function persistChosenLanguage(language) {
    SettingsSaveModule.save({ [LANGUAGE_FIELD]: language, [LANGUAGE_SOURCE_FIELD]: 'chosen' });
}
/**
 * The language the app should start in, or `null` when it must not touch the
 * language at all — because the player has chosen one, or because a stored
 * detection stands and no platform locale is present to supersede it.
 *
 * Returning `null` rather than "the stored value" keeps the decision and the
 * action separate: the caller changes the language only when there is something
 * to change, so the no-op case cannot accidentally fire an `i18n` reload.
 *
 * @param supported the locales this platform ships, **fallback first**.
 * @param platformLocale a tag from a platform that knows better than the
 *   browser does — CrazyGames' system info. Omitted, null or empty falls
 *   through to the browser, which is the right answer everywhere else AND the
 *   right answer on CrazyGames when the SDK is blocked or absent.
 *
 *   May be passed as a **function**, which is then called only if a stored
 *   CHOICE has not already settled the question (a stored detection does
 *   consult it — that is the Module 59 supersede). That is not an optimisation:
 *   it keeps "the player's choice wins" observable as an ordering rather than
 *   as a result that happens to be discarded afterwards, so a caller whose
 *   source has a cost or a side effect (a portal SDK call) genuinely does not
 *   pay it.
 * @param options see {@link InitialLanguageOptions.persist} — the one knob, and
 *   the one place the CrazyGames two-step boot is accounted for.
 */
export function initialLanguage(supported, platformLocale, options = {}) {
    const stored = SettingsSaveModule.loadStored();
    const storedLanguage = stored[LANGUAGE_FIELD];
    // A real choice wins forever — the original precedence rule, unchanged.
    if (storedLanguage !== undefined && stored[LANGUAGE_SOURCE_FIELD] === 'chosen')
        return null;
    if (storedLanguage !== undefined) {
        // Module 59 — a stored DETECTION is supersedable, by the platform alone.
        //
        // Detect-once persistence (WP-46.2) made the first boot's guess look like a
        // decision, and Playgama's Localization certification is where that bit:
        // the tool switches the platform language to German after the tester has
        // played (their flow guarantees stored state by then), and the game kept
        // the detected English — "language parameter was ignored on
        // initialization", failed for every tester, every time.
        //
        // So the stored value now carries provenance, and a detection yields to a
        // platform that currently says otherwise. The browser deliberately CANNOT
        // supersede — "the language stops changing under you" (the work-laptop
        // case that motivated WP-46.2) was always about the browser, and it still
        // holds: off-platform, a stored detection is as final as it ever was.
        //
        // A pre-provenance store (no `languageSource`) is treated as detected: at
        // the time this shipped, every launched platform resolves without a
        // platform locale (so nothing changes for it), and the embeds whose stores
        // could be superseded were not yet live — while the certification
        // environments that DID have stored state were all detections. The cost —
        // one supersede against a pre-provenance explicit choice — re-fixes itself
        // the moment the player chooses again.
        const supplied = typeof platformLocale === 'function' ? platformLocale() : platformLocale;
        if (!supplied)
            return null;
        const language = resolveLocale(supplied, supported);
        if (language === storedLanguage)
            return null;
        if (options.persist ?? true)
            persistDetected(language);
        _detectedThisBoot = true;
        return language;
    }
    const supplied = typeof platformLocale === 'function' ? platformLocale() : platformLocale;
    const browserTags = detectBrowserLocales();
    // Module 80 (WP-80.13) — no platform answer: the browser's WHOLE list. A
    // platform answer still wins outright (rule 4; Playgama certifies it).
    const language = supplied
        ? resolveLocale(supplied, supported)
        : (resolveBrowserLocales(browserTags, supported) ?? resolveLocale(null, supported));
    if (options.persist ?? true)
        persistDetected(language);
    _detectedThisBoot = true;
    return language;
}
/**
 * Module 50 (WP-50.21) — did THIS boot pick the language, or did the player?
 *
 * The first-launch language pill needs "the player has never chosen a language",
 * and the obvious test — asking storage — cannot answer it: detect-once
 * persistence (WP-46.2) writes the detected language during boot, so by the time
 * any UI renders, storage says `saved` on a session where nothing was ever
 * chosen. `describeLocaleResolution().source !== 'saved'` has exactly the same
 * problem for exactly the same reason, and it is the platform where the pill
 * matters most — CrazyGames, which persists the SDK answer — that it is wrong on.
 *
 * So the answer is captured where it is still true: {@link initialLanguage}
 * returns a language only when nothing was stored, which is the definition. No
 * new persisted key and no new writer to `SettingsSaveModule` (cross-cutting
 * rule 3) — this is a boot-scoped fact, and a reload re-derives it correctly by
 * finding the stored value and returning null.
 *
 * Monotonic within a boot on purpose. CrazyGames resolves twice — once from the
 * browser to render, once from the SDK to settle — and the second call runs
 * against storage the first deliberately did not write. Both see "nothing
 * stored", so both agree here; making it monotonic means they would still agree
 * if that ordering ever changed, and a later `false` could not erase a detection
 * that really happened.
 */
let _detectedThisBoot = false;
export function languageWasDetectedThisBoot() {
    return _detectedThisBoot;
}
/** Test seam — module state outlives a test file otherwise. */
export function resetDetectedThisBootForTest() {
    _detectedThisBoot = false;
}
/**
 * The same precedence order as {@link initialLanguage}, reporting *why* rather
 * than acting.
 *
 * Exists for Module 46's dev diagnostics (WP-46.4). Two properties matter and
 * both are why this is a sibling rather than an extra return value on
 * `initialLanguage`:
 *
 * - **It has no side effects.** It never persists and never calls a thunk, so
 *   logging cannot change what the app then does — a diagnostic that perturbs
 *   the thing it measures is worse than none.
 * - **Production never runs it.** `initialLanguage` stays a plain
 *   `string | null`, so no shipping code path computes a `source` nobody reads.
 *
 * The cost is that the ordering is written twice, which is a real risk and the
 * reason `initial-locale.test.ts` asserts the two agree on the same inputs.
 */
export function describeLocaleResolution(supported, platformLocale) {
    const storedSettings = SettingsSaveModule.loadStored();
    const stored = storedSettings[LANGUAGE_FIELD];
    // Module 59 — mirrors initialLanguage: a stored CHOICE always reports as
    // saved; a stored DETECTION reports as saved only when no platform locale is
    // present to supersede it (when one is, the platform branch below is what
    // initialLanguage would act on).
    if (stored !== undefined) {
        const supersedable = storedSettings[LANGUAGE_SOURCE_FIELD] !== 'chosen' && !!platformLocale;
        if (!supersedable)
            return { language: stored, source: 'saved', tag: stored };
    }
    if (platformLocale) {
        return {
            language: resolveLocale(platformLocale, supported),
            source: 'platform',
            tag: platformLocale,
        };
    }
    const hasNavigator = typeof navigator !== 'undefined';
    const list = hasNavigator ? navigator.languages : undefined;
    const fromList = Array.isArray(list)
        ? list.find((tag) => typeof tag === 'string' && tag)
        : undefined;
    if (fromList) {
        return {
            language: resolveLocale(fromList, supported),
            source: 'browser-languages',
            tag: fromList,
        };
    }
    const single = hasNavigator ? navigator.language : undefined;
    if (single) {
        return { language: resolveLocale(single, supported), source: 'browser-language', tag: single };
    }
    return { language: supported[0] ?? 'en', source: 'fallback', tag: null };
}
